import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, Play, CheckCircle, Clock, User, Settings, LogOut, Loader2, FileText, Download, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EBOOK_INFO = {
  "ebook-1": { title: "Stop Living Paycheck to Paycheck", image: "https://customer-assets.emergentagent.com/job_omw-academy/artifacts/ljfzsvtw_IMG_5405.png" },
  "ebook-2": { title: "Building Business Credit", image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=200" },
  "ebook-3": { title: "Starting Your LLC", image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=200" },
  "ebook-4": { title: "Business Planning Workbook", image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200" },
  "ebook-5": { title: "Bankruptcy to Breakthrough", image: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=200" },
  "ebook-6": { title: "The Entrepreneur Reset Blueprint", image: "https://static.prod-images.emergentagent.com/jobs/7884e44f-3dc0-40da-b443-283b24b01218/images/157ee318a0925bc5e7b8db1ae53669c40d6cced6f9f05292b66b73cb12f4b4de.png" }
};

const DashboardPage = () => {
  const { user, logout, loading: authLoading, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [purchasedEbooks, setPurchasedEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("courses");

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { state: { from: "/dashboard" } });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        const enrollmentsRes = await axios.get(`${API_URL}/api/enrollments`, {
          headers: getAuthHeader()
        });
        setEnrollments(enrollmentsRes.data);

        // Check which ebooks are purchased
        const ebookIds = ["ebook-1", "ebook-2", "ebook-3", "ebook-4", "ebook-5", "ebook-6"];
        const purchased = [];
        for (const id of ebookIds) {
          try {
            const res = await axios.get(`${API_URL}/api/ebooks/${id}/purchased`, {
              headers: getAuthHeader()
            });
            if (res.data.purchased) {
              purchased.push(id);
            }
          } catch (e) {
            // Not purchased
          }
        }
        setPurchasedEbooks(purchased);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, getAuthHeader]);

  const getProgress = (enrollment) => {
    const totalModules = 5; // Default module count
    const completed = Object.values(enrollment.progress || {}).filter(Boolean).length;
    return Math.round((completed / totalModules) * 100);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1E3A5F] py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-white/10 flex items-center justify-center text-white text-2xl font-bold">
                {user.full_name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Welcome back, {user.full_name?.split(' ')[0]}!
                </h1>
                <p className="text-gray-300">{user.email}</p>
              </div>
            </div>
            <Button 
              onClick={handleLogout}
              variant="outline" 
              className="border-white/30 text-white hover:bg-white/10"
              data-testid="dashboard-logout-btn"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-4 sticky top-24">
              <nav className="space-y-2">
                <button
                  onClick={() => setActiveTab("courses")}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === "courses" 
                      ? "bg-[#1E3A5F]/10 text-[#1E3A5F] font-medium" 
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                  data-testid="tab-courses"
                >
                  <BookOpen className="h-5 w-5" />
                  My Courses
                </button>
                <button
                  onClick={() => setActiveTab("ebooks")}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === "ebooks" 
                      ? "bg-[#1E3A5F]/10 text-[#1E3A5F] font-medium" 
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                  data-testid="tab-ebooks"
                >
                  <FileText className="h-5 w-5" />
                  My Ebooks
                  {purchasedEbooks.length > 0 && (
                    <span className="ml-auto bg-[#9D9045] text-white text-xs px-2 py-0.5 rounded-full">
                      {purchasedEbooks.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab("profile")}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === "profile" 
                      ? "bg-[#1E3A5F]/10 text-[#1E3A5F] font-medium" 
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                  data-testid="tab-profile"
                >
                  <User className="h-5 w-5" />
                  Profile
                </button>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === "courses" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    My Enrolled Courses
                  </h2>
                  <Link to="/school">
                    <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F]">
                      Browse More Courses
                    </Button>
                  </Link>
                </div>

                {enrollments.length === 0 ? (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <BookOpen className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No Courses Yet</h3>
                    <p className="text-gray-600 mb-6">
                      Start your learning journey by enrolling in one of our business courses.
                    </p>
                    <Link to="/school">
                      <Button className="bg-[#1E3A5F] hover:bg-[#162c47] text-white" data-testid="browse-courses-btn">
                        Browse Courses
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {enrollments.map((enrollment) => {
                      const progress = getProgress(enrollment);
                      
                      return (
                        <div 
                          key={enrollment.id} 
                          className="bg-white rounded-xl shadow-sm overflow-hidden"
                          data-testid={`enrollment-${enrollment.course_id}`}
                        >
                          <div className="p-6">
                            <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                              {enrollment.course_title}
                            </h3>
                            
                            <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                              <Clock className="h-4 w-4" />
                              <span>Enrolled {new Date(enrollment.enrolled_at).toLocaleDateString()}</span>
                            </div>
                            
                            <div className="space-y-2 mb-4">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Progress</span>
                                <span className="font-medium text-[#1E3A5F]">{progress}%</span>
                              </div>
                              <Progress value={progress} className="h-2" />
                            </div>
                            
                            <Link to={`/school/course/${enrollment.course_id}`}>
                              <Button className="w-full bg-[#1E3A5F] hover:bg-[#162c47] text-white">
                                {progress > 0 ? (
                                  <>
                                    <Play className="mr-2 h-4 w-4" />
                                    Continue Learning
                                  </>
                                ) : (
                                  <>
                                    <Play className="mr-2 h-4 w-4" />
                                    Start Course
                                  </>
                                )}
                              </Button>
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === "ebooks" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    My Purchased Ebooks
                  </h2>
                  <Link to="/ebooks">
                    <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F]">
                      Browse More Ebooks
                    </Button>
                  </Link>
                </div>

                {purchasedEbooks.length === 0 ? (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No Ebooks Yet</h3>
                    <p className="text-gray-600 mb-6">
                      Purchase an ebook to access it here anytime. View and print your digital library.
                    </p>
                    <Link to="/ebooks">
                      <Button className="bg-[#1E3A5F] hover:bg-[#162c47] text-white" data-testid="browse-ebooks-btn">
                        Browse Ebook Store
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {purchasedEbooks.map((ebookId) => {
                      const info = EBOOK_INFO[ebookId];
                      if (!info) return null;
                      
                      return (
                        <div 
                          key={ebookId} 
                          className="bg-white rounded-xl shadow-sm overflow-hidden flex"
                          data-testid={`ebook-${ebookId}`}
                        >
                          <div className="w-32 flex-shrink-0">
                            <img 
                              src={info.image}
                              alt={info.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="p-4 flex flex-col justify-between flex-1">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <ShieldCheck className="h-4 w-4 text-green-600" />
                                <span className="text-xs text-green-600 font-medium">Purchased</span>
                              </div>
                              <h3 className="text-lg font-semibold text-[#1E3A5F] line-clamp-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                                {info.title}
                              </h3>
                            </div>
                            <Link to={`/ebook/view/${ebookId}`}>
                              <Button className="w-full mt-3 bg-[#9D9045] hover:bg-[#8a7e3c] text-white" data-testid={`view-ebook-${ebookId}`}>
                                <Download className="mr-2 h-4 w-4" />
                                View / Print
                              </Button>
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === "profile" && (
              <div className="bg-white rounded-xl shadow-sm p-8">
                <h2 className="text-xl font-semibold text-[#1E3A5F] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Profile Information
                </h2>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
                      <p className="text-[#1E3A5F] font-medium">{user.full_name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Email Address</label>
                      <p className="text-[#1E3A5F] font-medium">{user.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Phone</label>
                      <p className="text-[#1E3A5F] font-medium">{user.phone || "Not provided"}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Member Since</label>
                      <p className="text-[#1E3A5F] font-medium">
                        {new Date(user.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="pt-6 border-t border-gray-100">
                    <h3 className="font-medium text-[#1E3A5F] mb-4">Enrolled Courses</h3>
                    <div className="flex flex-wrap gap-2">
                      {user.enrolled_courses?.length > 0 ? (
                        user.enrolled_courses.map((courseId) => (
                          <span 
                            key={courseId}
                            className="px-3 py-1 bg-[#1E3A5F]/10 text-[#1E3A5F] rounded-full text-sm"
                          >
                            {courseId}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-500">No courses enrolled yet</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
